import * as React from 'react';
import { isDefined, noop } from 'react-financial-charts';
import { isHoverForInteractiveType, saveNodeType, terminate } from 'react-financial-charts';
import {
  ClickableCircle,
  HoverTextNearMouse,
  MouseLocationIndicator,
} from '@react-financial-charts/interactive/lib/components/index';
import { EachTrendLine, InteractiveStraightLine, InteractiveText } from './Wrapper';
import type { TrendLineDragData } from './Wrapper/EachTrendLine';
import {
  calculateAngle,
  calculateBarRange,
  calculateDaysBetweenPoints,
  calculatePercentChange,
  calculatePriceRange,
} from '@/utils/duration';
import { getValueFromOverride } from '@/utils/utils';
import { getCombinedTrendLabel, getLabelOffset } from '@/utils/combinedText';
import type {
  ChartId,
  CoordDragOverride,
  DrawingSharedState,
  ExtensibleInfoLineRenderNode,
  HoverTextConfig,
  RenderNode,
  MoreProps,
  TrendLineSharedState,
} from '../../../types/commontypes';
import { ShapeType } from '../../../types/commontypes';
import { chartOrder } from '@/utils/chartConfig';
import { clampYToChart } from '@/index';
import type { YCoordinateClickedData } from '../../../contexts/ChartUIContext';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';
import { toLineRFCProps, toHorzVertLineRFCProps, RFC_RENDER_MODE } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';

export interface TrendLineProps {
  readonly snap: boolean;
  readonly enabled: boolean;
  readonly snapTo?: (datum: unknown) => number | number[];
  readonly shouldDisableSnap?: (e: React.MouseEvent) => boolean;
  readonly onStart: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  readonly onComplete?: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionstrokeOpacity?: number;
  readonly currentPositionRadius?: number;
  /**
   * Logical shape type handled by this wrapper instance. SubTask 8: ShapeRender
   * passes one of TREND_LINE / RAY / INFO_LINE / HORZ_LINE. Used for:
   * - sharedState.type comparison (preview condition)
   * - RFC_RENDER_MODE[shapeType] → RFC render mode ('XLINE' | 'LINE' | 'RAY')
   * - new shape creation in handleEnd
   */
  readonly shapeType?: ShapeType;
  hoverText?: Partial<HoverTextConfig>;
  readonly trends: RenderNode[];
  readonly direction?: string;
  readonly hoverId: string | null;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly yCoordinateClicked?: YCoordinateClickedData | undefined;
  readonly locked?: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly nonInteractiveStraightLine?: boolean; // If true, the line will not be interactive

  readonly chartId?: ChartId;
  readonly sharedState?: TrendLineSharedState;
  readonly setSharedState?: React.Dispatch<React.SetStateAction<DrawingSharedState>>;
  readonly timeFrame?: string;
  readonly isAnyShapeEnabled?: boolean;
}

/**
 * 드래그 중 임시 상태.
 * handleDragStart 시점엔 trend 전체(start/end 배열)가 저장되고,
 * handleDragLine 시점에 x1Value 등 좌표와 측정값이 덮어씌워진다.
 */
interface TrendLineDragOverride extends CoordDragOverride {
  barsRange?: number;
  daysBetween?: object;
  priceRange?: number;
  totalPercentChange?: number;
  angle?: number;
  [key: string]: unknown;
}

interface TrendLineState {
  override?: TrendLineDragOverride;
  trends?: RenderNode | RenderNode[] | null;
  lineInteractiveCursor?: string;
}

export class TrendLine extends React.Component<TrendLineProps, TrendLineState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!TrendLine._cachedDefaultProps) {
      TrendLine._cachedDefaultProps = {
        onStart: noop,
        onSelect: noop,
        currentPositionStroke: '#1e53e6',
        currentPositionstrokeOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 0,
        shouldDisableSnap: (e: React.MouseEvent) => e.button === 2 || e.shiftKey,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          bgHeight: 'auto',
          bgWidth: 'auto',
          text: 'Click to select object',
          selectedText: '',
        },
        trends: [],
      };
    }
    return TrendLine._cachedDefaultProps;
  }
  private getSelectionState: (...args: unknown[]) => unknown;
  private mouseMoved: boolean = false;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;

  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: TrendLineProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('trends').bind(this);

    this.state = {};
  }

  public render() {
    const {
      currentPositionstrokeOpacity,
      currentPositionRadius = TrendLine.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      hoverText,
      shouldDisableSnap,
      snap,
      snapTo,
      trends,
      shapeType,
      direction,
      onDoubleClick,
      chartId,
      sharedState,
      timeFrame,
      isAnyShapeEnabled,
    } = this.props;

    // Infer the shape type handled by this wrapper (fallback to TREND_LINE for legacy).
    const resolvedShapeType: ShapeType = shapeType ?? ShapeType.TREND_LINE;
    const rfcMode = RFC_RENDER_MODE[resolvedShapeType];

    // Active template node for preview (uses last used / default storage-shape values).
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(resolvedShapeType) as ExtensibleInfoLineRenderNode;
    const appearance =
      resolvedShapeType === ShapeType.HORZ_LINE || resolvedShapeType === ShapeType.VERT_LINE
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (toHorzVertLineRFCProps(activeNode as never) as ReturnType<typeof toLineRFCProps>)
        : toLineRFCProps(activeNode);

    const {
      strokeStyle,
      strokeWidth,
      extendLeft,
      extendRight,
      r,
      edgeFill,
      edgeStroke,
      edgeStrokeWidth,
      strokeDasharray,
      showAngle,
      showBarsRange,
      showDateTimeRange,
      showPercentChange,
      showPriceRange,
      bgStyle,
      textColor,
    } = appearance;
    const { override } = this.state;

    const sharedStartXY = sharedState?.startXY;
    const sharedEndXY = sharedState?.endXY;
    const sharedStateText = sharedState?.text;

    const tempCondition =
      sharedState?.type === resolvedShapeType &&
      chartId === sharedState?.chartId &&
      sharedEndXY &&
      sharedState?.status !== 'completed';

    const combinedText = tempCondition
      ? getCombinedTrendLabel({
          showPriceRange,
          showPercentChange,
          showBarsRange,
          showDateTimeRange,
          showAngle,
          priceRange: sharedStateText?.priceRange,
          totalPercentChange: sharedStateText?.totalPercentChange,
          barsRange: sharedStateText?.barsRange,
          daysBetween: sharedStateText?.daysBetween,
          angle: sharedStateText?.angle,
        })
      : undefined;

    const offset = getLabelOffset(sharedStartXY?.[1], sharedEndXY?.[1]);

    const hasValidLabel =
      Boolean(tempCondition) &&
      direction === undefined &&
      typeof combinedText === 'string' &&
      combinedText.trim().length > 0;

    const labelPosition: [number, number] | null =
      hasValidLabel && sharedStartXY && sharedEndXY
        ? [(sharedStartXY[0] + sharedEndXY[0]) / 2, (sharedStartXY[1] + sharedEndXY[1]) / 2 + offset]
        : null;

    const tempLine =
      tempCondition && sharedStartXY && sharedEndXY ? (
        <>
          <InteractiveStraightLine
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            type={rfcMode as 'XLINE' | 'RAY' | 'LINE'}
            x1Value={sharedStartXY[0]}
            y1Value={sharedStartXY[1]}
            x2Value={sharedEndXY[0]}
            y2Value={sharedEndXY[1]}
            strokeStyle={strokeStyle}
            strokeWidth={strokeWidth}
            extendLeft={extendLeft}
            extendRight={extendRight}
            strokeDasharray={strokeDasharray}
          />
          <ClickableCircle
            show={true}
            cx={sharedStartXY[0]}
            cy={sharedStartXY[1]}
            r={r}
            fillStyle={edgeFill}
            strokeStyle={edgeStroke}
            strokeWidth={edgeStrokeWidth}
            interactiveCursorClass="big-declined-arrow-cursor"
          />
          {hasValidLabel && labelPosition && (
            <InteractiveText
              selected={true}
              position={labelPosition}
              bgFillStyle={bgStyle ?? 'rgba(242, 242, 242, 0.8)'}
              bgStroke={bgStyle ?? 'rgba(242, 242, 242, 0.8)'}
              bgStrokeWidth={0}
              textFill={textColor ?? 'rgba(29, 31, 34, 1)'}
              fontFamily="Arial"
              fontStyle="normal"
              fontWeight="normal"
              fontSize={12}
              text={combinedText}
              showBackground={true}
              paddingX={10}
              paddingY={8}
            />
          )}
        </>
      ) : null;

    return (
      <g>
        {trends?.map((each, idx) => {
          if (each.hidden) return null;
          const eachMode = RFC_RENDER_MODE[each.type];

          const eachAppearanceRaw =
            each.type === ShapeType.HORZ_LINE || each.type === ShapeType.VERT_LINE
              ? (toHorzVertLineRFCProps(each) as Record<string, unknown>)
              : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                (toLineRFCProps(each as ExtensibleInfoLineRenderNode) as Record<string, unknown>);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const eachAppearance = eachAppearanceRaw as {
            strokeStyle: string;
            strokeWidth: number;
            strokeDasharray: string;
            edgeStroke: string;
            edgeFill: string;
            edgeStrokeWidth: number;
            r: number;
            bgStyle?: string;
            extendLeft?: boolean;
            extendRight?: boolean;
            arrowDirection?: string;
            showPriceRange?: boolean;
            showPercentChange?: boolean;
            showBarsRange?: boolean;
            showDateTimeRange?: boolean;
            showAngle?: boolean;
            alwaysShow?: boolean;
          };
          const hoverTextWithDefault = {
            ...TrendLine.defaultProps.hoverText,
            ...hoverText,
          };

          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const overrideOrEach = override?.uuid === each.uuid ? override : (each as unknown as TrendLineDragOverride);
          const { barsRange, daysBetween, priceRange, totalPercentChange, angle } = overrideOrEach ?? {};

          const leftArrow = eachAppearance.arrowDirection === 'left' || eachAppearance.arrowDirection === 'both';
          const rightArrow = eachAppearance.arrowDirection === 'right' || eachAppearance.arrowDirection === 'both';
          const additionalProps =
            direction !== 'Horz'
              ? {
                  barsRange,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  daysBetween: daysBetween as { days: number; hours: number; minutes: number } | undefined,
                  priceRange,
                  totalPercentChange,
                  angle,
                  showPriceRange: eachAppearance.showPriceRange,
                  showPercentChange: eachAppearance.showPercentChange,
                  showBarsRange: eachAppearance.showBarsRange,
                  showDateTimeRange: eachAppearance.showDateTimeRange,
                  alwaysShow: eachAppearance.alwaysShow,
                  showAngle: eachAppearance.showAngle,
                }
              : {
                  direction: direction,
                };
          // Return the EachTrendLine component with merged commonProps and additionalProps
          const eachAsStartEnd = each as RenderNode & {
            start?: [number, number];
            end?: [number, number];
            yValue?: number;
          };
          // HorzLine 복원 시 start/end가 없을 수 있음 — yValue로 폴백하여 XLINE이 정상 렌더링되도록 함
          const yFallback = eachAsStartEnd.yValue ?? 0;
          const eachStart = eachAsStartEnd.start ?? [0, yFallback];
          const eachEnd = eachAsStartEnd.end ?? [1, yFallback];
          return (
            <EachTrendLine
              {...additionalProps}
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              uuid={each.uuid}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              type={eachMode as 'XLINE' | 'RAY' | 'LINE'}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              selected={(each as unknown as { selected?: boolean }).selected}
              x1Value={getValueFromOverride(override, each.uuid, 'x1Value', eachStart[0])}
              y1Value={getValueFromOverride(override, each.uuid, 'y1Value', eachStart[1])}
              x2Value={getValueFromOverride(override, each.uuid, 'x2Value', eachEnd[0])}
              y2Value={getValueFromOverride(override, each.uuid, 'y2Value', eachEnd[1])}
              strokeStyle={eachAppearance.strokeStyle}
              strokeWidth={eachAppearance.strokeWidth}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeOpacity={eachAppearanceRaw['strokeOpacity'] as number}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              strokeDasharray={eachAppearance.strokeDasharray as never}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              edgeStrokeWidth={eachAppearance.edgeStrokeWidth}
              r={eachAppearance.r}
              bgStyle={eachAppearance.bgStyle}
              textColor={textColor}
              extendRight={eachAppearance.extendRight}
              extendLeft={eachAppearance.extendLeft}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              arrowDirection={eachAppearance.arrowDirection as 'left' | 'right' | 'both' | 'none' | undefined}
              leftArrow={leftArrow}
              rightArrow={rightArrow}
              showPriceRange={eachAppearance.showPriceRange}
              showPercentChange={eachAppearance.showPercentChange}
              showBarsRange={eachAppearance.showBarsRange}
              showDateTimeRange={eachAppearance.showDateTimeRange}
              showAngle={eachAppearance.showAngle}
              alwaysShow={eachAppearance.alwaysShow}
              timeFrame={timeFrame}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="big-declined-arrow-cursor"
              lineInteractiveCursor={'react-financial-charts-pointer-cursor'}
              onSelect={this.handleSelect}
              onDragStart={this.handleDragStart}
              onHover={this.handleHover}
              locked={each.locked}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState ?? {}}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          // onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
        />
      </g>
    );
  }

  componentDidUpdate(prevProps: TrendLineProps) {
    const { yCoordinateClicked, sharedState, chartId, direction } = this.props;
    // Only create HorzLine if this chart matches the clicked chart
    if (
      yCoordinateClicked !== prevProps.yCoordinateClicked &&
      yCoordinateClicked !== undefined &&
      direction === 'Horz' &&
      yCoordinateClicked.chartId === chartId &&
      chartId === sharedState?.chartId
    ) {
      // xyValue[0] is not used in handleStart for Horz lines, so we can pass [0, yCoordinateClicked.value]
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
      this.handleStart({} as React.MouseEvent, [0, yCoordinateClicked.value], {} as MoreProps);
    }
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { trends } = this.props;
    const newTrends = trends.map((trends, _idx) => ({
      ...trends,
      selected: trends.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect !== undefined) {
      onSelect(e, newTrends, moreProps);
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const xy = xyValue as [number, number];
    const { trends, direction, chartId, sharedState, enabled } = this.props;
    const { onComplete, onStart } = this.props;
    const createTrend = (
      start: number[],
      end: number[],
      additionalProps: Record<string, unknown>,
      creationShapeType: ShapeType,
    ) => {
      const last = getActiveTemplateNode(creationShapeType);
      const defaults = defaultShapePropertyValues[creationShapeType];
      const initial = { ...defaults, ...last };
      return [
        ...trends.map(d => ({ ...d, selected: false })),
        {
          ...initial,
          ...additionalProps,
          start,
          end,
          selected: true,
          type: creationShapeType,
        },
      ];
    };

    if (direction === 'Horz') {
      const yValue = xy[1];
      const { xScale } = moreProps;
      const [x1Value, x2Value] = xScale?.domain?.() ?? [103, 207];
      const cxVisible = x2Value - 10;
      const targetChartId = sharedState?.chartId || chartId;
      const shapeUuid = crypto.randomUUID();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newTrends = createTrend(
        [x1Value, yValue],
        [cxVisible, yValue],
        {
          yValue,
          chartId: targetChartId,
          uuid: shapeUuid,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
        ShapeType.HORZ_LINE,
      ) as RenderNode[];
      this.setState({ trends: newTrends }, () => onComplete?.(e, newTrends, moreProps));
    } else {
      if (!enabled) return;
      if (sharedState?.status !== 'started') {
        this.mouseMoved = false;
        onStart?.(e, xy, moreProps);
      } else {
        const shapesChartId = sharedState?.chartId;
        // Only do min/max clamp if sharedState exists
        const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xy[1]);
        this.handleEnd(e, [xy[0], yValue], moreProps);
      }
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { trends, shapeType, direction, chartId, sharedState, enabled, timeFrame } = this.props;
    if (!enabled) return;
    if (sharedState?.status === 'started' && sharedState?.startXY && direction !== 'Horz') {
      this.mouseMoved = false;
      const startXY = sharedState.startXY;
      const barsRange = calculateBarRange(moreProps, startXY[0], xyValue[0]);
      const daysBetween = calculateDaysBetweenPoints(barsRange, timeFrame!);
      const priceRange = calculatePriceRange(startXY[1], xyValue[1]);
      const totalPercentChange = calculatePercentChange(startXY[1], xyValue[1]);
      const angle = calculateAngle(moreProps, startXY, xyValue);

      const _shapesChartId = sharedState?.chartId || chartId;
      const shapeUuid = crypto.randomUUID();
      const resolvedShapeType: ShapeType = shapeType ?? ShapeType.TREND_LINE;
      const lastProps = getActiveTemplateNode(resolvedShapeType);
      const defaults = defaultShapePropertyValues[resolvedShapeType];
      const initial = { ...defaults, ...lastProps };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newTrends = [
        ...trends.map(d => ({ ...d, selected: false })),
        {
          ...initial,
          start: startXY,
          end: xyValue,
          selected: true,
          type: resolvedShapeType,
          uuid: shapeUuid,
          barsRange,
          daysBetween,
          priceRange,
          totalPercentChange,
          angle,
          hidden: false,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          alwaysShow: true,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          chartId: (sharedState?.chartId || chartId) as `chartId_${string}`,
        },
      ] as RenderNode[];
      this.setState(
        {
          trends: newTrends,
        },
        () => {
          const { onComplete } = this.props;
          if (onComplete !== undefined) {
            onComplete(e, newTrends, moreProps);
          }
        },
      );
    }
  };

  private readonly handleDragLineComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    const { direction, trends, onDragComplete, hoverId } = this.props;
    if (!override || override.uuid !== hoverId) return;
    this.mouseMoved = false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const updatedTrends = this.updateTrends(direction, override, trends, moreProps) as RenderNode[];
    onDragComplete?.(e, updatedTrends, moreProps);
    this.setState({ override: undefined });
  };

  private readonly handleDrawLine = (_: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => {
    const { direction, sharedState, chartId, setSharedState, enabled, timeFrame } = this.props;
    const start = sharedState?.startXY;
    if (!isDefined(sharedState)) {
      return;
    }
    if ((sharedState?.startXY && sharedState?.status !== 'started' && direction !== 'Horz') || !enabled) {
      return;
    }
    if (!start) return;

    const startChartId = sharedState?.chartId;
    const pointerChartId = moreProps.currentCharts?.[0];

    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    this.mouseMoved = true;
    const [x] = xyValue;
    let [, y] = xyValue;

    // Clamp Y if pointer is on a different chart
    y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);

    const barsRange = calculateBarRange(moreProps, start[0], x);
    const daysBetween = calculateDaysBetweenPoints(barsRange, timeFrame!);
    const priceRange = calculatePriceRange(start[1], y);
    const totalPercentChange = calculatePercentChange(start[1], y);
    const angle = calculateAngle(moreProps, start, [x, y]);

    setSharedState?.({
      ...sharedState,
      endXY: [x, y],
      text: {
        barsRange,
        daysBetween,
        priceRange,
        totalPercentChange,
        angle,
      },
    });
  };

  private readonly handleDragLine = (
    _: React.MouseEvent,
    uuid: string | undefined,
    newXYValue: TrendLineDragData,
    moreProps: MoreProps,
  ) => {
    const { hoverId, direction, timeFrame } = this.props;
    const { override } = this.state;
    if (uuid === undefined) return;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    const { x1Value, y1Value, x2Value, y2Value } = newXYValue;
    const isHorzVert = direction === 'Horz';
    let extraParams;
    if (!isHorzVert) {
      const barsRange = calculateBarRange(moreProps, x1Value, x2Value);
      const daysBetween = calculateDaysBetweenPoints(barsRange, timeFrame!);
      const priceRange = calculatePriceRange(y1Value, y2Value);
      const totalPercentChange = calculatePercentChange(y1Value, y2Value);
      const angle = calculateAngle(moreProps, [x1Value, y1Value], [x2Value, y2Value]);
      extraParams = { barsRange, daysBetween, priceRange, totalPercentChange, angle };
    }
    this.mouseMoved = true;
    this.setState({
      override: {
        uuid,
        ...newXYValue,
        ...extraParams,
      },
    });
  };

  private updateTrends(
    direction: string | undefined,
    override: TrendLineDragOverride,
    trends: RenderNode[],
    moreProps: MoreProps,
  ) {
    if (direction === 'Horz') {
      return this.updateTrendsForHorzVert(override, trends);
    } else {
      return this.updateTrendsForBars(override, trends, moreProps);
    }
  }

  private updateTrendsForHorzVert(override: TrendLineDragOverride, trends: RenderNode[]) {
    return trends.map(each =>
      each.uuid === override.uuid
        ? {
            ...each,
            start: [override.x1Value, override.y1Value],
            end: [override.x2Value, override.y2Value],
            selected: true,
            alwaysShow: true,
            updatedAt: Date.now(),
          }
        : {
            ...each,
            selected: false,
          },
    );
  }

  private updateTrendsForBars(override: TrendLineDragOverride, trends: RenderNode[], _moreProps: MoreProps) {
    const { barsRange, daysBetween, priceRange, totalPercentChange, angle, x1Value, x2Value, y1Value, y2Value } =
      override;
    if (!isDefined(x1Value) || !isDefined(y1Value) || !isDefined(x2Value) || !isDefined(y2Value)) {
      return trends;
    }
    return trends.map(each =>
      each.uuid === override.uuid
        ? {
            ...each,
            start: [x1Value, y1Value],
            end: [x2Value, y2Value],
            selected: true,
            updatedAt: Date.now(),
            barsRange,
            daysBetween,
            priceRange,
            totalPercentChange,
            angle,
          }
        : {
            ...each,
            selected: false,
          },
    );
  }

  private readonly handleDragStart = (uuid: string) => {
    const trend = this.props.trends.find(t => t.uuid === uuid);
    if (!trend) return;

    this.setState({
      override: {
        ...trend,
        uuid,
      },
    });
  };
}
